import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Skeleton, Collapse, Tooltip,
  useTheme, useMediaQuery,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getPeers } from "../../../api/stocks_api";
import { getRecentlyViewed } from "../../../api/api";
import RecentlyViewedWidget from "../Widgets/recentlyViewedBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fmtL } from "../../../utils/currency";

/* ══════════════════════════════════════════════
   GRADE HELPERS
══════════════════════════════════════════════ */
const gradeBackground = (grade) => {
  const map = {
    "A+": "var(--color-grade-a-plus)", "A": "var(--color-grade-a)", "A-": "var(--color-grade-a-minus)",
    "B+": "var(--color-grade-b-plus)", "B": "var(--color-grade-b)", "B-": "var(--color-grade-b-minus)",
    "C+": "var(--color-grade-c-plus)", "C": "var(--color-grade-c)", "C-": "var(--color-grade-c-minus)",
    "D+": "var(--color-grade-d-plus)", "D": "var(--color-grade-d)", "D-": "var(--color-grade-d-minus)",
    "E": "var(--color-grade-e)",
    "F": "var(--color-grade-f)",
  };
  return map[grade] ?? "var(--color-grade-unknown)";
};

/* ══════════════════════════════════════════════
   SKELETON GRID  — matches card fixed height
══════════════════════════════════════════════ */
const CARD_HEIGHT = 148;

const PeersSkeleton = () => (
  <Box sx={{
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
    gap: 2,
  }}>
    {[...Array(6)].map((_, i) => (
      <Box key={i} sx={{
        height: CARD_HEIGHT,
        p: 2,
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--color-border-primary)",
        bgcolor: "var(--color-bg-secondary)",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Skeleton variant="circular" width={28} height={28} sx={{ bgcolor: "var(--color-surface-hover)", flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={20} sx={{ bgcolor: "var(--color-surface-hover)" }} />
            <Skeleton variant="text" width="70%" height={16} sx={{ bgcolor: "var(--color-surface-hover)" }} />
          </Box>
        </Box>
        <Skeleton variant="text" width="100%" height={18} sx={{ bgcolor: "var(--color-surface-hover)" }} />
        <Skeleton variant="text" width="100%" height={18} sx={{ bgcolor: "var(--color-surface-hover)" }} />
      </Box>
    ))}
  </Box>
);

/* ══════════════════════════════════════════════
   PEER CARD  — fixed height, name truncated
══════════════════════════════════════════════ */
const PeerCard = ({ peer, onNavigate }) => (
  <Paper
    elevation={0}
    onClick={() => onNavigate(`/stocks/${peer.ticker}/details/summary`)}
    sx={{
      height: CARD_HEIGHT,           // ← every card exactly the same height
      p: 2,
      display: "flex",
      flexDirection: "column",
      borderRadius: "var(--radius-lg)",
      bgcolor: "var(--color-bg-secondary)",
      border: "1px solid var(--color-border-primary)",
      cursor: "pointer",
      transition: "var(--transition-normal)",
      boxSizing: "border-box",
      overflow: "hidden",            // ← nothing can push outside
      "&:hover": {
        borderColor: "var(--color-primary)",
        transform: "translateY(-3px)",
        boxShadow: "var(--shadow-lg)",
        bgcolor: "var(--color-surface-hover)",
      },
    }}
  >
    {/* ── Logo + ticker + name ── */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      {peer.logo_url ? (
        <Box
          component="img"
          src={peer.logo_url}
          alt={peer.ticker}
          sx={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <Box sx={{
          width: 28, height: 28, borderRadius: 1, flexShrink: 0,
          bgcolor: "var(--color-primary-lighter)",
          border: "1px solid var(--color-primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-bold)", color: "var(--color-primary)" }}>
            {peer.ticker?.charAt(0)}
          </Typography>
        </Box>
      )}

      {/* ticker + company name — both clipped, name shows tooltip */}
      <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
        <Typography sx={{
          fontSize: "var(--font-size-base)",
          fontWeight: "var(--font-weight-bold)",
          color: "var(--color-primary)",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {peer.ticker}
        </Typography>

        <Tooltip
          title={peer.company_name || ""}
          placement="top"
          arrow
          enterDelay={400}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "var(--color-surface)",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border-primary)",
                boxShadow: "var(--shadow-md)",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-medium)",
              },
            },
            arrow: { sx: { color: "var(--color-border-primary)" } },
          }}
        >
          <Typography sx={{
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
            fontWeight: "var(--font-weight-medium)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          }}>
            {peer.company_name}
          </Typography>
        </Tooltip>
      </Box>
    </Box>

    {/* ── Divider ── */}
    <Box sx={{ height: "1px", bgcolor: "var(--color-border-primary)", my: 1.5, flexShrink: 0 }} />

    {/* ── FA Score + Grade — always at the bottom ── */}
    <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 0.75 }}>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-semibold)",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}>
          FA Score
        </Typography>
        <Typography sx={{
          fontSize: "var(--font-size-base)",
          fontWeight: "var(--font-weight-bold)",
          color: "var(--color-info)",
          fontVariantNumeric: "tabular-nums",
        }}>
          {peer.score != null ? fmtL(peer.score, 2) : "—"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-semibold)",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}>
          FA Grade
        </Typography>
        {peer.grade ? (
          <Box sx={{ px: 1.5, py: 0.25, borderRadius: "var(--radius-sm)", bgcolor: gradeBackground(peer.grade) }}>
            <Typography sx={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-bold)", color: "#fff" }}>
              {peer.grade}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)" }}>—</Typography>
        )}
      </Box>
    </Box>
  </Paper>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const Peers = ({ stock }) => {
  const { ticker } = useParams();
  const navigate   = useNavigate();
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down("sm"));

  const [peers, setPeers]                   = useState([]);
  const [loadingPeers, setLoadingPeers]     = useState(true);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loadingRecent, setLoadingRecent]   = useState(true);
  const [sidebarOpen, setSidebarOpen]       = useState(false);

  useEffect(() => {
    if (!ticker) return;
    (async () => {
      setLoadingPeers(true);
      const res = await getPeers(ticker);
      setPeers(res?.success && res.data?.response_data ? res.data.response_data : []);
      setLoadingPeers(false);
    })();
  }, [ticker]);

  useEffect(() => {
    if (!stock?.ticker) return;
    (async () => {
      setLoadingRecent(true);
      const res = await getRecentlyViewed();
      if (res?.success && Array.isArray(res?.data))
        setRecentlyViewed(res.data.filter((i) => i.ticker !== stock.ticker));
      setLoadingRecent(false);
    })();
  }, [stock?.ticker]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", lg: "row" },
      alignItems: "flex-start",
      gap: { xs: 2, sm: 3 },
      width: "100%",
      px: { xs: 1.5, sm: 2.5, md: 3 },
      py: { xs: 1.5, sm: 2 },
      boxSizing: "border-box",
    }}>

      {/* ══════════════ MAIN COLUMN ══════════════ */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "var(--radius-xl)",
            p: { xs: 2, sm: 3 },
            background: "linear-gradient(135deg, var(--color-surface) 0%, var(--color-bg-secondary) 100%)",
            border: "1px solid var(--color-border-primary)",
            boxShadow: "var(--shadow-xl)",
          }}
        >
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography sx={{
              fontSize: "var(--font-size-lg)",
              fontWeight: "var(--font-weight-bold)",
              color: "var(--color-text-primary)",
              mb: 0.5,
            }}>
              Sector Peers
            </Typography>
            <Typography sx={{
              fontSize: "var(--font-size-base)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-muted)",
            }}>
              Companies in the same sector as{" "}
              <Box component="span" sx={{ color: "var(--color-primary)", fontWeight: "var(--font-weight-bold)" }}>
                {ticker}
              </Box>
            </Typography>
          </Box>

          {!loadingPeers && peers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{
                display: "inline-flex", px: 1.5, py: 0.4,
                borderRadius: "var(--radius-sm)",
                bgcolor: "var(--color-surface-hover)",
                border: "1px solid var(--color-border-primary)",
              }}>
                <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)" }}>
                  {peers.length} companies
                </Typography>
              </Box>
            </Box>
          )}

          {loadingPeers ? (
            <PeersSkeleton />
          ) : peers.length === 0 ? (
            <Box sx={{
              textAlign: "center", py: 8,
              bgcolor: "var(--color-bg-secondary)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--color-border-primary)",
            }}>
              <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--color-text-muted)" }}>
                No peer companies found for {ticker}
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
              gap: 2,
            }}>
              {peers.map((peer) => (
                <PeerCard key={peer.ticker} peer={peer} onNavigate={navigate} />
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* ══════════════ SIDEBAR ══════════════ */}
      <Box sx={{
        width: { xs: "100%", lg: 320 },
        flexShrink: 0,
        position: { lg: "sticky" },
        top: { lg: 80 },
        alignSelf: { lg: "flex-start" },
        maxHeight: { lg: "calc(100vh - 100px)" },
        overflowY: { lg: "auto" },
      }}>
        {isMobile ? (
          <Box sx={{ border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
            <Box
              onClick={() => setSidebarOpen((p) => !p)}
              sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                px: 2, py: 1.5, cursor: "pointer",
                bgcolor: "var(--color-surface)",
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
              }}
            >
              <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>
                Recently Viewed
              </Typography>
              <ExpandMoreIcon sx={{
                fontSize: 20, color: "var(--color-text-muted)",
                transform: sidebarOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "var(--transition-fast)",
              }} />
            </Box>
            <Collapse in={sidebarOpen}>
              <Box sx={{ bgcolor: "var(--color-surface)", borderTop: "1px solid var(--color-border-primary)" }}>
                <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
              </Box>
            </Collapse>
          </Box>
        ) : (
          <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
        )}
      </Box>
    </Box>
  );
};

export default Peers;